// pages/Shop/EditStaff.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import './AddnewStaffpage.css';
import PageAccessPicker from '../../components/PageAccessPicker';

const extractErrorMessage = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;

  const parts = [];
  const collect = (value, label) => {
    if (value === null || value === undefined || value === '') return;
    if (typeof value === 'string' || typeof value === 'number') {
      parts.push(label ? `${label}: ${value}` : String(value));
    } else if (Array.isArray(value)) {
      value.forEach((item) => collect(item, label));
    } else if (typeof value === 'object') {
      Object.keys(value).forEach((key) => collect(value[key], key));
    }
  };

  collect(data.error);
  collect(data.detail);
  collect(data.message);
  collect(data.details);
  collect(data.errors);

  if (parts.length === 0) {
    Object.keys(data).forEach((key) => collect(data[key], key));
  }

  return parts.join(' | ');
};

const EditStaff = ({ onClose, onUpdate, isPage = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // URL se staff ID milegi
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');

  // Get shops from location state
  const locationState = location.state || {};
  const shopsFromState = locationState.shops || [];
  const selectedShopIdFromState = locationState.selectedShopId || '';

  const [shops, setShops] = useState(shopsFromState);
  const [selectedShopId, setSelectedShopId] = useState(selectedShopIdFromState || '');
  const [availableCities, setAvailableCities] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '', // Edit mein password optional hoga
    phone: '',
    userType: 'STAFF',
    houseNumber: '',
    streetNumber: '',
    area: '',
    city: '',
    selectedShopId: selectedShopIdFromState || ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Yeh state page permissions ke liye hai
  const [allowedPages, setAllowedPages] = useState([]);

  const getAuthToken = () => {
    const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
    for (let key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          if (parsed.access || parsed.token || parsed.access_token) {
            return parsed.access || parsed.token || parsed.access_token;
          }
          if (parsed.token && typeof parsed.token === 'object') {
            return parsed.token.access || parsed.token.token || parsed.token.access_token;
          }
        } catch (e) {
          return storedData;
        }
      }
    }
    return null;
  };

  // 1. Shops fetch karein agar state se nahi mile hain
  useEffect(() => {
    if (shopsFromState.length === 0) {
      fetchShops();
    }
  }, []);

  // 2. Staff Data aur Permissions fetch karein
  useEffect(() => {
    if (id) {
      fetchStaffData(id);
    }
  }, [id]);

  const fetchShops = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch('https://super-backend.hira-tech.co.uk/user/shops/', {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setShops(data);
      }
    } catch (err) {
      console.error('Error fetching shops:', err);
    }
  };

  const fetchStaffData = async (staffId) => {
    try {
      setFetchLoading(true);
      const authToken = getAuthToken();

      // Staff Details fetch karein
      const response = await fetch(`https://super-backend.hira-tech.co.uk/user/users/${staffId}/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const data = await response.json();

        setFormData({
          firstName: data.first_Name || '',
          lastName: data.last_Name || '',
          email: data.email || '',
          password: '',
          phone: data.phone_Number || '',
          userType: data.type || 'STAFF',
          houseNumber: data.address?.house_Number || '',
          streetNumber: data.address?.street_Number || '',
          area: data.address?.area || '',
          city: data.address?.city || data.selected_city || '',
          selectedShopId: data.shop || data.shop_id || ''
        });

        setSelectedShopId(data.shop || data.shop_id || '');

        // Load cities if shop is selected
        if (data.shop || data.shop_id) {
          loadCitiesForShop(data.shop || data.shop_id, data.address?.city || data.selected_city);
        }
      }

      // ✅ 3. Permissions fetch karein
      const permResponse = await fetch(`https://super-backend.hira-tech.co.uk/user/${staffId}/permissions/`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (permResponse.ok) {
        const permData = await permResponse.json();
        // Checkbox pre-select karne ke liye allowedPages state update karein
        setAllowedPages(permData.allowed_pages || permData.permissions || []);
      }

    } catch (err) {
      console.error('Error fetching staff data:', err);
      Swal.fire('Error', 'Failed to load staff data.', 'error');
    } finally {
      setFetchLoading(false);
    }
  };

  const loadCitiesForShop = async (shopId, currentCity = '') => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const shop = shops.find(s => s.shop_id.toString() === shopId.toString());

      if (shop && shop.cities && shop.cities.length > 0) {
        setAvailableCities(shop.cities);
        return;
      }

      const response = await fetch(`https://super-backend.hira-tech.co.uk/user/shop-cities/?shop_id=${shopId}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableCities(data.cities || []);
      }
    } catch (err) {
      console.error('Error loading cities:', err);
    }
  };

  const handleShopChange = (e) => {
    const shopId = e.target.value;
    setSelectedShopId(shopId);
    setFormData(prev => ({ ...prev, city: '', selectedShopId: shopId }));
    loadCitiesForShop(shopId);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!selectedShopId) newErrors.selectedShopId = 'Please select a shop';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      Swal.fire({ icon: 'error', title: 'Validation Error', text: 'Please fill required fields correctly', confirmButtonColor: '#004aad' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    Swal.fire({
      title: 'Updating Staff...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const authToken = getAuthToken();

      const apiData = {
        first_Name: formData.firstName,
        last_Name: formData.lastName,
        email: formData.email,
        phone_Number: formData.phone,
        type: formData.userType.toUpperCase(),
        shop_id: selectedShopId,
        selected_city: formData.city,
        address: {
          house_Number: formData.houseNumber,
          street_Number: formData.streetNumber,
          area: formData.area,
          city: formData.city
        }
      };

      // Password update karein agar user ne naya daala hai
      if (formData.password.trim() !== '') {
        apiData.password = formData.password;
      }

      // Update Staff Details
      const response = await fetch(`https://super-backend.hira-tech.co.uk/user/users/${id}/`, {
        method: 'PUT', // Ya PATCH agar backend support karta hai
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        // ✅ 4. Permissions update karein
        await fetch(`https://super-backend.hira-tech.co.uk/user/${id}/permissions/update/`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ allowed_pages: allowedPages })
        });

        Swal.close();
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Staff member updated successfully!',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          navigate(`/shop/${selectedShopId}/staff`);
        });
      } else {
        const errorData = await response.json();
        Swal.close();
        Swal.fire('Error', extractErrorMessage(errorData), 'error');
      }
    } catch (error) {
      Swal.close();
      Swal.fire('Error', 'Network error. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (fetchLoading) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div className="staff-form page-form">
      <div className="form-header">
        <h2>Edit Staff User Account</h2>
        <button className="back-button-top" onClick={() => navigate(`/shop/${selectedShopId}/staff`)}>
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Select Shop </label>
            <select
              name="selectedShopId"
              value={selectedShopId}
              onChange={handleShopChange}
              className={errors.selectedShopId ? 'error' : ''}
            >
              <option value="">-- Select a Shop --</option>
              {shops.map(shop => (
                <option key={shop.shop_id} value={shop.shop_id.toString()}>
                  {shop.shop_name}
                </option>
              ))}
            </select>
            {errors.selectedShopId && <span className="error-message">{errors.selectedShopId}</span>}
          </div>

          <div className="form-group">
            <label>First Name </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={errors.firstName ? 'error' : ''}
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>

          <div className="form-group">
            <label>Last Name </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={errors.lastName ? 'error' : ''}
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>User Type </label>
            <select name="userType" value={formData.userType} onChange={handleChange}>
              <option value="STAFF">STAFF</option>
            </select>
          </div>

          <div className="form-group">
            <label>Email </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label>Password (Leave blank to keep current)</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter new password to change"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Phone Number </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={errors.phone ? 'error' : ''}
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="form-group">
            <label>City </label>
            <select
              name="city"
              value={formData.city}
              onChange={handleChange}
              disabled={!selectedShopId || availableCities.length === 0}
            >
              <option value="">
                {selectedShopId
                  ? (availableCities.length > 0 ? '-- Select a City --' : 'No cities available')
                  : '-- Select a Shop First --'}
              </option>
              {availableCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>House Number </label>
            <input
              type="text"
              name="houseNumber"
              value={formData.houseNumber}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Street Number </label>
            <input
              type="text"
              name="streetNumber"
              value={formData.streetNumber}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Area </label>
            <input
              type="text"
              name="area"
              value={formData.area}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Postal Code (Optional)</label>
            <input
              type="text"
              name="postalCode"
              placeholder="Enter Postal Code"
            />
          </div>
        </div>

        {/* ✅ Yahan PageAccessPicker ka prop 'value' set hai jo pre-checked karega */}
        {['STAFF', 'ADMIN'].includes(formData.userType.toUpperCase()) && (
          <PageAccessPicker
            value={allowedPages}
            onChange={setAllowedPages}
          />
        )}

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={() => navigate(`/shop/${selectedShopId}/staff`)}>
            Cancel
          </button>
          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Updating...' : 'Update Staff'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditStaff;